import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Utensils, Users, Award, Building2, User, MapPin } from "lucide-react";
import { DEELNEMERS } from "@/constants/testIds";
import SectionLabel from "@/components/SectionLabel";
import Zelfscan from "@/components/Zelfscan";
import usePageMeta from "@/hooks/usePageMeta";

const aanmeldOpties = [
  {
    key: "gemeente",
    icon: Building2,
    label: "Via mijn gemeente",
    text:
      "Je hebt een uitkering en je gemeente werkt (of overweegt te werken) samen met LevelLeven. Wij leggen contact met je casemanager.",
    role: "deelnemer",
    query: "?rol=deelnemer",
  },
  {
    key: "particulier",
    icon: User,
    label: "Zonder uitkering",
    text:
      "Je zit niet in een uitkering, maar herkent jezelf toch in het verhaal. Ook dan is deelname mogelijk, particulier of via je werkgever/verzekeraar.",
    role: "deelnemer-particulier",
    query: "?rol=deelnemer-particulier",
  },
  {
    key: "wachtlijst",
    icon: MapPin,
    label: "Andere gemeente, wachtlijst",
    text:
      "Je gemeente werkt (nog) niet met ons. Meld je aan voor de wachtlijst, zodra we in jouw regio starten, laten we het weten.",
    role: "wachtlijst",
    query: "?rol=wachtlijst",
  },
];

const HERO_IMG =
  "https://images.pexels.com/photos/28155741/pexels-photo-28155741.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const SUPPORT_IMG =
  "https://images.unsplash.com/photo-1604881991405-b273c7a4386a?crop=entropy&cs=srgb&fm=jpg&q=85";

const ervaringen = [
  {
    icon: ShieldCheck,
    title: "Veiligheid & verbinding",
    text: "Een vaste groep, twee vaste trainers en een buddy. Je voelt je gezien en gehoord, veiligheid is het fundament.",
  },
  {
    icon: Utensils,
    title: "Warmte & menselijkheid",
    text: "Elke bijeenkomst inclusief een warme lunch. Geen extraatje, maar een bewuste keuze: samen eten verbindt.",
  },
  {
    icon: Users,
    title: "Vaste structuur",
    text: "Tweewekelijkse bijeenkomsten op een vaste plek in de wijk. Begrijpelijke taal, geen bureaucratie.",
  },
  {
    icon: Award,
    title: "Certificering",
    text: "Het traject sluit af met een persoonlijk certificaat. Iets wat je hebt verdiend, zwart op wit.",
  },
];

const stories = [
  {
    quote:
      "Ik dacht dat ik lui was. Nu weet ik dat ik opgebrand was. Voor het eerst kijk ik weer vooruit.",
    name: "Esther",
  },
  {
    quote:
      "Voor het eerst voelde ik dat iemand niet probeerde mij te repareren, maar mij echt wilde zien.",
    name: "Mark",
  },
  {
    quote:
      "Ik dacht dat werken nooit meer ging lukken. Inmiddels heb ik een eigen ritme, en een toekomst.",
    name: "Layla",
  },
];

export default function Deelnemers() {
  usePageMeta({
    title: "Voor deelnemers, LevelLeven",
    description:
      "Twaalf weken, in jouw tempo, met een vaste groep en twee trainers. Doe de zelfscan.",
  });
  return (
    <div>
      {/* HERO */}
      <section className="py-12 sm:py-20">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 reveal order-2 lg:order-1">
              <SectionLabel>Voor jou</SectionLabel>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#1D2A22] font-light">
                Misschien sta je al jaren{" "}
                <span className="italic text-[#E06B3F]">stil</span>.
              </h1>
              <div className="mt-10 space-y-5 text-xl sm:text-2xl text-[#4A5D4E] font-light leading-relaxed max-w-2xl">
                <p>Niet omdat je niet wilt.</p>
                <p>Maar omdat het fundament ontbreekt.</p>
                <p>Geen dagstructuur. Geen veilig pad vooruit.</p>
                <p className="text-[#1D2A22]">
                  LevelLeven legt dat fundament, samen met jou.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5 reveal order-1 lg:order-2">
              <div className="rounded-3xl overflow-hidden aspect-[4/5] bg-[#EFECE5]">
                <img src={HERO_IMG} alt="Stil moment in natuurlijk licht" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WAT JE ERVAART */}
      <section className="py-24 sm:py-32 bg-[#EFECE5]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="mb-16 max-w-2xl reveal">
            <SectionLabel>Wat je concreet ervaart</SectionLabel>
            <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1D2A22] font-light">
              Geen toets. Geen oordeel.{" "}
              <span className="italic text-[#E06B3F]">Wel ruimte.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ervaringen.map((b, i) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  className="reveal bg-[#FAF7F1] rounded-2xl p-8 border border-[#E5E1D8] hover:border-[#E06B3F] transition-colors"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className="w-12 h-12 rounded-full bg-[#E06B3F]/10 text-[#E06B3F] flex items-center justify-center mb-6">
                    <Icon size={20} />
                  </div>
                  <p className="font-heading text-2xl text-[#1D2A22] leading-tight">{b.title}</p>
                  <p className="mt-3 text-[#4A5D4E] leading-relaxed">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOE WERKT HET */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5 reveal">
              <SectionLabel>LevelLeven START, twaalf weken</SectionLabel>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1D2A22] font-light">
                Twaalf weken.
                <br />
                <span className="italic text-[#E06B3F]">Jouw tempo.</span>
              </h2>
              <p className="mt-6 text-[#4A5D4E] font-light leading-relaxed max-w-md">
                80% van het programma is oefening en ervaring. Je leert niet
                óver jezelf, je ontdekt jezelf.
              </p>
            </div>
            <div className="md:col-span-7 reveal">
              <ol className="divide-y divide-[#E5E1D8]">
                {[
                  { n: "01", t: "Startpunt & Rugzak", d: "Week 1-3. Kennismaken, je vertrekpunt vastleggen. Wat neem je mee?" },
                  { n: "02", t: "Rem", d: "Week 4-6. Zien wat je tegenhoudt, zonder oordeel." },
                  { n: "03", t: "Vonk & Speelruimte", d: "Week 7-9. Nieuwe dingen proberen en oefenen." },
                  { n: "04", t: "Volgende beweging", d: "Week 10-12. Terugkijken en samen een volgende stap kiezen die bij jou past." },
                ].map((s) => (
                  <li key={s.n} className="py-6 flex items-baseline gap-8">
                    <span className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] w-10">{s.n}</span>
                    <div>
                      <p className="font-heading text-2xl text-[#1D2A22] font-light leading-snug">
                        {s.t}
                        {s.optioneel && (
                          <span className="ml-3 text-[10px] uppercase tracking-[0.3em] text-[#E06B3F] bg-[#E06B3F]/10 rounded-full px-2 py-1 align-middle">
                            Optioneel
                          </span>
                        )}
                      </p>
                      <p className="mt-1 text-[#4A5D4E]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ZELFSCAN */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-4 reveal lg:sticky lg:top-28">
              <SectionLabel>Zelfscan</SectionLabel>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1D2A22] font-light">
                Vier vragen.
                <br />
                <span className="italic text-[#E06B3F]">Geen goed of fout.</span>
              </h2>
              <p className="mt-6 text-[#4A5D4E] font-light leading-relaxed">
                Vier korte vragen om te ontdekken of LevelLeven past bij waar
                jij nu staat. Anoniem, er wordt niets opgeslagen.
              </p>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 reveal">
              <Zelfscan />
            </div>
          </div>
        </div>
      </section>

      {/* AANMELDEN */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="mb-14 max-w-2xl reveal">
            <SectionLabel>Meld je aan</SectionLabel>
            <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1D2A22] font-light">
              Drie manieren om{" "}
              <span className="italic text-[#E06B3F]">mee te doen</span>.
            </h2>
            <p className="mt-6 text-[#4A5D4E] font-light leading-relaxed">
              LevelLeven is er niet alleen voor mensen in een uitkering.
              Herken je jezelf, of woon je in een gemeente waar wij nog niet
              actief zijn? Meld je alsnog aan, via de route die bij jou past.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {aanmeldOpties.map((o, i) => {
              const Icon = o.icon;
              return (
                <Link
                  key={o.key}
                  to={`/contact${o.query}`}
                  data-testid={`aanmeld-${o.key}`}
                  className="reveal group bg-[#EFECE5] hover:bg-[#1D2A22] hover:text-[#FAF7F1] rounded-2xl p-7 sm:p-8 border border-[#E5E1D8] transition-colors duration-300"
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <div className="w-12 h-12 rounded-full bg-[#E06B3F]/10 group-hover:bg-[#E06B3F] text-[#E06B3F] group-hover:text-white flex items-center justify-center mb-6 transition-colors">
                    <Icon size={20} />
                  </div>
                  <p className="font-heading text-2xl leading-tight">{o.label}</p>
                  <p className="mt-3 text-sm text-[#4A5D4E] group-hover:text-[#FAF7F1]/75 leading-relaxed">
                    {o.text}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm">
                    Meld je aan
                    <ArrowUpRight size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* VERHALEN */}
      <section className="py-24 sm:py-32 bg-[#EFECE5]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-4 reveal lg:sticky lg:top-28">
              <SectionLabel>Ervaringsverhalen</SectionLabel>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1D2A22] font-light">
                Echte mensen.
                <br />
                <span className="italic text-[#E06B3F]">Geen acteurs.</span>
              </h2>
              <div className="mt-10 rounded-3xl overflow-hidden aspect-[5/4]">
                <img src={SUPPORT_IMG} alt="In gesprek bij koffie" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 space-y-10">
              {stories.map((s, i) => (
                <figure key={i} className="reveal border-t border-[#E5E1D8] pt-10">
                  <p className="font-heading italic text-2xl sm:text-3xl text-[#1D2A22] leading-snug font-light">
                    "{s.quote}"
                  </p>
                  <figcaption className="mt-5 text-xs uppercase tracking-[0.25em] text-[#7A8276]">
                    {s.name}, deelnemer
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="rounded-3xl bg-[#1D2A22] text-[#FAF7F1] px-8 sm:px-16 py-20 lg:py-28 text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D19B72] mb-6">
              Eén stap is genoeg
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight font-light max-w-3xl mx-auto">
              Wil je weer in beweging?{" "}
              <span className="italic text-[#D19B72]">Wij lopen graag een stukje met je mee.</span>
            </h2>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid={DEELNEMERS.ctaAanmelden}
                className="group inline-flex items-center justify-center gap-3 bg-[#E06B3F] hover:bg-[#FAF7F1] hover:text-[#1D2A22] text-white rounded-full px-8 py-4 transition-colors duration-300"
              >
                <span className="text-base font-medium">Meld je aan</span>
                <ArrowUpRight size={18} />
              </Link>
              <Link
                to="/faq"
                data-testid={DEELNEMERS.ctaInfo}
                className="inline-flex items-center justify-center gap-3 border border-[#FAF7F1]/30 text-[#FAF7F1] hover:bg-[#FAF7F1] hover:text-[#1D2A22] rounded-full px-8 py-4 transition-colors duration-300"
              >
                <span className="text-base font-medium">Lees veelgestelde vragen</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
